import { Badge, Table, Text } from "@mantine/core";
import { useCallback } from "react";
import type { KeyboardEvent } from "react";
import type { ShippedCase } from "../../services/workflows.service";
import { formatLabDateTime } from "../../utils/labTime";

interface ShippedCaseTableRowProps {
  shippedCase: ShippedCase;
  onOpen: (shippedCase: ShippedCase) => void;
}

export default function ShippedCaseTableRow({
  shippedCase,
  onOpen,
}: ShippedCaseTableRowProps) {
  const handleOpen = useCallback(() => {
    onOpen(shippedCase);
  }, [onOpen, shippedCase]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTableRowElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen(shippedCase);
      }
    },
    [onOpen, shippedCase]
  );

  return (
    <Table.Tr
      onClick={handleOpen}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="button"
      aria-label={`Open shipment details for ${shippedCase.caseNumber}`}
      style={{ cursor: "pointer" }}
    >
      <Table.Td>
        <Text size="sm" fw={500}>
          {shippedCase.caseNumber}
        </Text>
      </Table.Td>
      <Table.Td>
        {shippedCase.patient
          ? `${shippedCase.patient.firstName} ${shippedCase.patient.lastName}`
          : "N/A"}
      </Table.Td>
      <Table.Td>{formatLabDateTime(shippedCase.shippedAt)}</Table.Td>
      <Table.Td>{shippedCase.pathologist?.fullName ?? "N/A"}</Table.Td>
      <Table.Td>{shippedCase.sendingPractice?.name ?? "N/A"}</Table.Td>
      <Table.Td>
        {shippedCase.currentStatus?.name ? (
          <Badge variant="light">{shippedCase.currentStatus.name}</Badge>
        ) : (
          "N/A"
        )}
      </Table.Td>
      <Table.Td>
        {shippedCase.shippingTrackingNumber || (
          <Text size="sm" c="dimmed" fs="italic">
            Not entered
          </Text>
        )}
      </Table.Td>
    </Table.Tr>
  );
}
